import { t } from "components/CentralDataMangement/translation/Translation";
import Title from "components/common/atoms/Title";
import OfficeTargetTable from "../common/OfficeTargetTable";
import {
  CooAgreementDetails,
  OMTargetAchievedData,
  OfficeTarget,
  ProspectCallDetails,
  StarterDetails,
  StopperDetails,
  TargetAchievedTable,
  User,
  VacancyDetails,
  VisitsDetails,
} from "../Annotations";
import { useEffect, useMemo, useState } from "react";
import OMTargetTable from "../common/OMTargetTable";
import VacanciesTable from "../common/VacanciesTable";
import VisitsTable from "../common/VisitsTable";
import CooAgreementTable from "../common/CooAgreementTable";
import ProspectCallsTable from "../common/ProspectCallsTable";
import TableTitle from "components/common/atoms/TableTitle";
import StartersTable from "../common/StartersTable";
import StoppersTable from "../common/StoppersTable";
import RecruiterTargetTable from "../common/RecruiterTargetTable";
import LabelWithTextAreaField from "components/common/molecules/LabelWithTextAreaField";
// import secureLocalStorage from "react-secure-storage";
import EncryptDecryptService from "components/common/services/EncryptDecryptService";
import CustomNotify from "components/common/atoms/CustomNotify";
import { ApiCall } from "components/common/services/ApiServices";
import { EDIT_MONTHLY_REPORTS, UPDATE_MONTHLY_REPORTS } from "../../../../routes/ApiEndpoints";
import { ADMIN, AREA_MANAGER, CENTRAL_DATA_MANAGEMENT_MICROSERVICE, OFFICE_MANAGER, SUPER_ADMIN } from "Constants";
import LoadingIcon from "components/common/utlis/LoadingIcon";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Button from "components/common/atoms/Button";
import { useSelector } from "react-redux";
import { selectAuth } from "features/auth/AuthSlice";
import ValidationDetails from "../common/ValidationDetails";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";

interface TargetData {
  officeQTarget: OfficeTarget;
  officeTarget: OfficeTarget;
  targets: OMTargetAchievedData;
}

interface ReportsData {
  periodId: number | null;
  vacancies: VacancyDetails[];
  foreignVacancies: VacancyDetails[];
  visits: VisitsDetails[];
  cooAgreement: CooAgreementDetails[];
  prospectCalls: ProspectCallDetails[];
  starters: StarterDetails[];
  stoppers: StopperDetails[];
  from?: string | null;
  to?: string | null;
  omName?: string;
}

interface RecruiterData {
  created_by: number | string;
  recruiterTargets?: TargetAchievedTable;
}

interface RecruitersData {
  recruiters: User[];
  recTargets: RecruiterData[];
  starters: StarterDetails[];
  stoppers: StopperDetails[];
}

interface Analysis {
  analysis: string;
  smile_moments: string;
  growth_points: string;
  thoughts_comments: string;
  om_analysis: string;
  om_name: string;
  om_confirmed_at: string | null;
  am_analysis: string;
  am_name: string;
  am_confirmed_at: string | null;
}

const MonthlyReports: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [targetData, setTargetData] = useState<TargetData>();
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportsData>({
    periodId: null,
    vacancies: [],
    foreignVacancies: [],
    visits: [],
    prospectCalls: [],
    cooAgreement: [],
    starters: [],
    stoppers: []
  });
  const [recruiters, setRecruiters] = useState<RecruitersData>({
    recruiters: [],
    recTargets: [],
    starters: [],
    stoppers: [],
  });
  const [currentRecData, setCurrentRecData] = useState<RecruiterData>();
  const [analysisData, setAnalysisData] = useState<Analysis>({
    analysis: "",
    smile_moments: "",
    growth_points: "",
    thoughts_comments: "",
    am_analysis: "",
    om_analysis: "",
    om_name: "",
    om_confirmed_at: null,
    am_name: "",
    am_confirmed_at: null
  });

  const authUser = useSelector(selectAuth);
  // let data = secureLocalStorage.getItem('omMonthlyReport') as string; // Get the 'data' query param as a string
  const [searchParams] = useSearchParams();
  const data = searchParams.get("data");
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData();
    }, 3000); // 3 seconds

    return () => clearTimeout(timeout); // Cleanup the timeout on component unmount
  }, []);

  const fetchData = async () => {
    let parsedData = {};
    try {
      if (data) {
        // Decrypt the recruiterData
        let decryptedData = decodeURIComponent(EncryptDecryptService.decryptData(data));

        // If decryptedData is in JSON format, parse it
        parsedData = JSON.parse(decryptedData);
      }

    } catch (error) {
      CustomNotify({
        type: "error",
        message: t("Something went wrong"),
      });
      setLoading(false);
      return null;
    }

    const response = await ApiCall.service(
      EDIT_MONTHLY_REPORTS,
      "POST",
      { ...parsedData, type: "officeManager" },
      false,
      CENTRAL_DATA_MANAGEMENT_MICROSERVICE
    );

    if (response?.status === 200) {
      setTargetData((prevData) => (response?.data?.targetData));
      setReports(response?.data?.reportTypes);
      setRecruiters(response?.data?.recruiters);
      setCurrentRecData({ created_by: "global" });
      if (response?.data?.analysis) {
        setAnalysisData(response?.data?.analysis);
      }
    }else{
      CustomNotify({
        type: "error",
        message: "Some error occured"
      });
    }
    setLoading(false);
  }
  const foreignOffice = targetData?.officeQTarget?.isForeign

  const setCurrentRecruiter = (id: number | string) => {
    const recruiter = recruiters?.recTargets &&
      Object.values(recruiters?.recTargets).find(
        (data) => data.created_by === id
      );
    setCurrentRecData(recruiter);
  };

  const getStarters = (id: number | string) => {
    return recruiters.starters.filter((data) => data.created_by === id);
  };

  const getStoppers = (id: number | string) => {
    return recruiters.stoppers.filter((data) => data.created_by === id);
  };

  const getRecName = useMemo(
    () => (recruiterId: number | string) => {
      const recruiter = recruiters.recruiters.find((r) => r.id === recruiterId);
      return recruiter?.name ?? "";
    },
    [recruiters.recruiters]
  );

  const handleAnalysisFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target as HTMLInputElement;

    setAnalysisData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent, isValidate: boolean = false) => {
    e.preventDefault();
    setLoading(true);

    let parsedData = {};
    try {
      if (data) {
        // Decrypt the recruiterData
        let decryptedData = decodeURIComponent(EncryptDecryptService.decryptData(data));

        // If decryptedData is in JSON format, parse it
        parsedData = JSON.parse(decryptedData);
      }

    } catch (error) {
      CustomNotify({
        type: "error",
        message: t("Something went wrong"),
      });
      setLoading(false);
      return null;
    }

    const payload = {
      type: "officeManager",
      periodId: reports?.periodId,
      analysis: analysisData,
      isValidate: isValidate,
      reportData: { ...parsedData, type: "officeManager" }
    };

    const url = UPDATE_MONTHLY_REPORTS;
    const response = await ApiCall.service(
      url,
      "POST",
      payload,
      false,
      CENTRAL_DATA_MANAGEMENT_MICROSERVICE
    );
    if (response?.status === 200) {
      CustomNotify({ type: "success", message: response?.msg });
      navigate("/manage-reports");
    } else {
      CustomNotify({ type: "error", message: t("Something went wrong") })
    }
    setLoading(false);
  };

  const isCurrentMonth = useMemo(() => {
    try {
      if (!data) return false;
      const parsedData = JSON.parse(decodeURIComponent(EncryptDecryptService.decryptData(data)));
      const now = new Date();
      return Number(parsedData.periodNumber) === (now.getMonth() + 1) && Number(parsedData.year) === now.getFullYear();
    } catch {
      return false;
    }
  }, [data]);

  const canValidate = () => {
    if (authUser.role === OFFICE_MANAGER && !analysisData?.om_confirmed_at) {
      return true;
    } else if (authUser.role === AREA_MANAGER && analysisData?.om_confirmed_at && !analysisData?.am_confirmed_at) {
      return true;
    }
    return false;
  }

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Reports",
          read: true,
        },
      ]}
      renderNoAccess={true}
    >
      <Title title={`${t("Reporting (monthly office manager)")} - ${targetData?.officeQTarget?.officeName ?? ""} (${reports?.from ?? "-"} to ${reports?.to ?? "-"})`
      } />

      {loading ? (
        <div style={{ height: "38vw" }} className="d-flex align-items-center justify-content-center">
          <LoadingIcon
            iconType="bars"
            color="#e55496"
            className="m-auto"
            height={"5vw"}
            width={"5vw"}
          />
        </div>

      ) : (
        <>
          <div className="row">
            <div className="col-md-4 mb-3">
              <OfficeTargetTable
                data={targetData?.officeTarget}
                tableTitle={t("Office")}
              />
            </div>
            <div className="col-md-4 mb-3">
              <OfficeTargetTable
                data={targetData?.officeQTarget}
                tableTitle={t("Office Q")}
              />
            </div>
            <div className="col-md-4 mb-3">
              <OMTargetTable data={targetData?.targets} tableTitle={reports?.omName} isForeign={targetData?.officeQTarget?.isForeign}/>
            </div>
            {!foreignOffice &&
            <div className="col-md-6">
              <VacanciesTable data={reports.vacancies} tableTitle={t("Vacancies")} />
            </div>
            }
            <div className="col-md-6">
              <VacanciesTable data={reports.foreignVacancies} isFroTable={true} tableTitle={t("Vacancies FRO")} />
            </div>
            <div className="col-md-6">
              <VisitsTable data={reports.visits} tableTitle={t("Visits")} />
              <div>
                <ProspectCallsTable
                  data={reports.prospectCalls}
                  tableTitle={t("Prospect call")}
                />
              </div>
            </div>
            <div className="col-md-6">
              <CooAgreementTable
                data={reports.cooAgreement}
                tableTitle={t("Cooperation agreement")}
              />
            </div>

          </div>
          <div className="row">
            <div className="col-md-2 OMRecriterTableStickyWrapper scrollBarDesign">
              <table className="table mb-0 table-bordered position-sticky OMRecriterTableSticky">
                <thead>
                  <tr className="align-middle border-bottom TableHeader">
                    <th className="text-start ps-3">{t("Recruiters")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    onClick={(e) => { setCurrentRecData({ created_by: "global" }) }}
                    key={-1}
                  >
                    <td
                      className={`cursor-pointer ${"global" === currentRecData?.created_by ? "activeRecruiter" : ""}`}
                    >
                      {"Global"}
                    </td>
                  </tr>
                  {recruiters.recruiters.map((recruiter: User, index) => (
                    <tr
                      onClick={(e) => {
                        setCurrentRecruiter(recruiter.id);
                      }}
                      key={index}
                      className="cursor-pointer"
                    >
                      <td
                        className={`cursor-pointer ${recruiter.id === currentRecData?.created_by
                          ? "activeRecruiter"
                          : ""
                          }`}
                      >
                        {recruiter.name}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="col-md-10">
              {currentRecData?.created_by === "global" ? (
                <>
                  <TableTitle title={"Global"} />
                  <StartersTable
                    data={reports.starters}
                    view="om"
                    tableTitle={t("Starters")}
                  />
                  <StoppersTable
                    data={reports.stoppers}
                    view="om"
                    tableTitle={t("Stopper")}
                  />
                </>
              ) : (
                <>
                  <TableTitle title={getRecName(currentRecData?.created_by ?? 0)} />
                  <RecruiterTargetTable data={currentRecData?.recruiterTargets} />
                  <StartersTable
                    data={getStarters(currentRecData?.created_by ?? 0)}
                    view="om"
                    tableTitle={t("Starters")}
                  />
                  <StoppersTable
                    data={getStoppers(currentRecData?.created_by ?? 0)}
                    view="om"
                    tableTitle={t("Stopper")}
                  />
                </>
              )}
            </div>
            <div className="row equal-cols" style={{zIndex:3, backgroundColor: 'white'}}>
              <Title title={t("Analysis")} />
              {authUser.role === AREA_MANAGER && analysisData?.om_confirmed_at &&
                <ValidationDetails validatedBy={analysisData?.om_name} validatedAt={analysisData?.om_confirmed_at} />
              }
              <div className="col-7 mt-2">
                <div className="row">
                  <div className="col-6">
                    <LabelWithTextAreaField
                      name="analysis"
                      handleChange={handleAnalysisFieldChange}
                      label={t("Make an analysis of your month!")}
                      value={analysisData.analysis}
                      isDisabled={authUser.role === OFFICE_MANAGER ? !canValidate() : true}
                    />
                  </div>
                  <div className="col-6">
                    <LabelWithTextAreaField
                      name="smile_moments"
                      handleChange={handleAnalysisFieldChange}
                      label={t(`Share your "smile" moments with us!`)}
                      value={analysisData.smile_moments}
                      isDisabled={authUser.role === OFFICE_MANAGER ? !canValidate() : true}
                    />
                  </div>
                  <div className="col-6">
                    <LabelWithTextAreaField
                      name="growth_points"
                      handleChange={handleAnalysisFieldChange}
                      label={t("Share your growth points with us")}
                      value={analysisData.growth_points}
                      isDisabled={authUser.role === OFFICE_MANAGER ? !canValidate() : true}
                    />
                  </div>
                  <div className="col-6">
                    <LabelWithTextAreaField
                      name="thoughts_comments"
                      handleChange={handleAnalysisFieldChange}
                      label={t("Share your thoughts and comments with us")}
                      value={analysisData.thoughts_comments}
                      isDisabled={authUser.role === OFFICE_MANAGER ? !canValidate() : true}
                    />
                  </div>
                </div>
              </div>
              <div className="col-5">
                <LabelWithTextAreaField
                  name="om_analysis"
                  handleChange={handleAnalysisFieldChange}
                  label={t("Feedback office manager")}
                  value={analysisData.om_analysis}
                  isDisabled={authUser.role === OFFICE_MANAGER ? !canValidate() : true}
                />
                <LabelWithTextAreaField
                  name="am_analysis"
                  handleChange={handleAnalysisFieldChange}
                  label={t("Feedback region Manager")}
                  value={analysisData.am_analysis}
                  isDisabled={authUser.role === AREA_MANAGER ? !canValidate() : true}
                />
              </div>
            </div>
            <div className="row py-4">
              <div className="col-md-4 align-self-center">
                <Link
                  to="/manage-reports"
                  className=" back-btn text-decoration-underline"
                >
                  {t("Back")}
                </Link>
              </div>
              {/* <AccessControl
                requiredPermissions={[
                  {
                    permission: "Daily turnover",
                    create: true,
                    update: true,
                  },
                ]}
                actions={true}
                strict={false}
              > */}
              <div className="col-md-8 d-flex justify-content-end align-items-end">
                {canValidate() ? (
                  !loading ? (
                    <>
                      {!isCurrentMonth && (
                        <Button
                          title={t("Validate")}
                          type="submit"
                          className="form-button shadow-none me-2"
                          handleClick={(e) => handleSubmit(e, true)}
                        />
                      )}
                      <Button
                        title={t("Save")}
                        type="submit"
                        className="form-button shadow-none"
                        handleClick={(e) => handleSubmit(e)}
                      />
                    </>
                  ) : (
                    <LoadingIcon
                      iconType="bars"
                      color="#ff4dae"
                      className="float-end"
                      width={"3vw"}
                      height={"3vw"}
                    />
                  )) : null
                }
              </div>
              {/* </AccessControl> */}
            </div>
          </div>
        </>
      )}
    </AccessControl>
  );
};

export default MonthlyReports;
